import {
  ArrowRightLeft,
  CheckCircle2,
  ClipboardList,
  Combine,
  Loader2,
  Minus,
  Move,
  PauseCircle,
  Plus,
  Split,
  Trash2,
  User,
  UserCircle,
  UtensilsCrossed,
} from 'lucide-react';

import { useMemo } from 'react';

import ActionButton from '../../../../components/Buttons/ActionButton';
import Can from '../../../../components/auth/Can';
import SelectField from '../../../../components/Forms/Input/SelectField';
import RestaurantOrderBillingActions from '../../billing/RestaurantOrderBillingActions';

import {
  RestaurantOrderDTO,
  RestaurantOrderItemDTO,
  RestaurantOrderServiceType,
  RestaurantOrderStatus,
  RestaurantPartyOption,
} from '../../../../types/restaurant/RestaurantOrderTypes';
import { RestaurantKitchenStationPrintState } from '../../../../types/restaurant/RestaurantKitchenOrderTypes';
import { RestaurantTableDTO } from '../../../../types/restaurant/RestaurantTableTypes';
import {
  formatOrderMoney,
  orderItemName,
  orderLabels,
  serviceTypeLabels,
} from '../orderPresentation';

interface CurrentOrderPanelProps {
  selectedTable?: RestaurantTableDTO | null;
  order?: RestaurantOrderDTO | null;
  serviceType: RestaurantOrderServiceType;
  customers: RestaurantPartyOption[];
  waiters: RestaurantPartyOption[];
  customerId: number | '';
  waiterId: number | '';
  /** Solo soporte puede elegir un mesero distinto; el resto usa su sesión. */
  canChooseWaiter?: boolean;
  /** Etiqueta del usuario de la sesión (se muestra como mesero cuando no puede elegir). */
  sessionUserLabel?: string;
  notes: string;
  activeItems: RestaurantOrderItemDTO[];
  busy: boolean;
  kitchenPrintStates?: RestaurantKitchenStationPrintState[];
  loadingBilling: boolean;
  startingBilling: boolean;
  billing: any;
  billingMessage?: string;
  onCustomerChange: (value: number | '') => void;
  onOpenCustomerSelector: () => void;
  onWaiterChange: (value: number | '') => void;
  onNotesChange: (value: string) => void;
  onCreate: () => void;
  onCancelItem: (item: RestaurantOrderItemDTO) => void;
  onUpdateQuantity: (itemId: number, quantity: number) => void;
  onUpdateComboQuantity?: (comboInstanceId: string, quantity: number) => void;
  onCancelOrder: () => void;
  onConfirmOrder: () => void;
  onOpenMenu: () => void;
  onOpenBilling: () => void;
  onAddMore: () => void;
  onContinueBilling: () => void;
  /** Cuando es false, el restaurante no usa cocina: la orden abierta se cobra directo. */
  kitchenWorkflowEnabled?: boolean;
  posFlags?: {
    splitBillEnabled: boolean;
    allowTableTransfer: boolean;
    allowMergeOrders: boolean;
    allowMoveOrderItems: boolean;
  };
  onTransferTable?: () => void;
  onMoveItems?: () => void;
  onMergeOrders?: () => void;
  onSplitBill?: () => void;
}

const orderBadgeClass = (status: RestaurantOrderStatus) => {
  if (status === 'OPEN') return 'bg-primary/10 text-primary';
  if (status === 'CONFIRMED') return 'bg-success/10 text-success';
  if (status === 'CANCELLED') return 'bg-danger/10 text-danger';

  return 'bg-gray-2 text-bodydark2 dark:bg-meta-4';
};

const CurrentOrderPanel = ({
  selectedTable,
  order,
  serviceType,
  customers,
  waiters,
  customerId,
  waiterId,
  canChooseWaiter = false,
  sessionUserLabel,
  notes,
  activeItems,
  busy,
  kitchenPrintStates = [],
  loadingBilling,
  startingBilling,
  billing,
  billingMessage,
  onCustomerChange,
  onOpenCustomerSelector,
  onWaiterChange,
  onNotesChange,
  onCreate,
  onCancelItem,
  onUpdateQuantity,
  onUpdateComboQuantity,
  onCancelOrder,
  onConfirmOrder,
  onOpenMenu,
  onOpenBilling,
  onAddMore,
  onContinueBilling,
  kitchenWorkflowEnabled = true,
  posFlags,
  onTransferTable,
  onMoveItems,
  onMergeOrders,
  onSplitBill,
}: CurrentOrderPanelProps) => {
  const hasPendingItems = activeItems.some(
    (item) => item.editable === true && item.sentToKitchen === false
  );
  // La orden ya generó comanda de cocina (aunque ahora esté reabierta para "agregar más").
  const wasDispatched = (order?.items ?? []).some(
    (item) => item.sentToKitchen === true
  );

  const serviceOpen =
    order != null &&
    ['OPEN', 'CONFIRMED', 'IN_KITCHEN', 'READY'].includes(order.status);
  const hasSplit = (order?.checks?.length ?? 0) > 0;

  type OrderRow =
    | { type: 'item'; item: RestaurantOrderItemDTO }
    | {
        type: 'combo';
        key: string;
        name: string;
        components: RestaurantOrderItemDTO[];
        total: number;
        sentToKitchen: boolean;
        pending: boolean;
      };

  const orderRows = useMemo<OrderRow[]>(() => {
    const rows: OrderRow[] = [];
    const seen = new Set<string>();
    activeItems.forEach((item) => {
      const comboKey =
        item.comboInstanceId != null ? String(item.comboInstanceId) : null;
      if (!comboKey) {
        rows.push({ type: 'item', item });
        return;
      }
      if (seen.has(comboKey)) return;
      seen.add(comboKey);
      const components = activeItems.filter(
        (candidate) => String(candidate.comboInstanceId) === comboKey
      );
      rows.push({
        type: 'combo',
        key: comboKey,
        name: components[0]?.comboName || 'Combo',
        components,
        total: components.reduce(
          (sum, component) =>
            sum + Number(component.subtotal ?? component.total ?? 0),
          0
        ),
        sentToKitchen: components.some((component) => component.sentToKitchen),
        pending: components.some(
          (component) =>
            component.editable === true && component.sentToKitchen === false
        ),
      });
    });
    return rows;
  }, [activeItems]);
  const showToolbar =
    serviceOpen &&
    (posFlags?.allowTableTransfer ||
      posFlags?.allowMoveOrderItems ||
      posFlags?.allowMergeOrders ||
      posFlags?.splitBillEnabled);

  return (
    <section className="flex min-h-[640px] flex-col bg-white dark:bg-boxdark">
      <div className="border-b border-stroke bg-gray-2/50 p-4 dark:border-strokedark dark:bg-meta-4/20">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-semibold text-black dark:text-white">
              Orden actual
            </h2>
            <p className="mt-0.5 text-xs text-bodydark2">
              {order?.tableId != null
                ? `${order.tableName ?? selectedTable?.name ?? ''} · ${
                    order.tableCode ?? selectedTable?.code ?? ''
                  }`
                : order
                ? serviceTypeLabels[order.serviceType]
                : serviceType !== 'DINE_IN'
                ? serviceTypeLabels[serviceType]
                : selectedTable
                ? `${selectedTable.name} · ${selectedTable.code}`
                : 'Seleccione una mesa'}
            </p>
          </div>

          {order && (
            <span
              className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${orderBadgeClass(
                order.status
              )}`}
            >
              {orderLabels[order.status]}
            </span>
          )}
        </div>

        {order && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2 rounded-lg border border-stroke bg-white px-3 py-2 dark:border-strokedark dark:bg-boxdark">
              <span className="rounded-md bg-primary/10 p-1.5 text-primary">
                <User className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-bodydark2">
                  Cliente
                </p>
                <p className="truncate text-xs font-medium text-black dark:text-white">
                  {order.customerName ||
                    (order.customerId
                      ? `Cliente #${order.customerId}`
                      : 'Consumidor final')}
                </p>
              </div>
              {['OPEN', 'CONFIRMED', 'IN_KITCHEN', 'READY'].includes(
                order.status
              ) && (
                <Can anyOf={['RESTAURANT_ORDERS_OPEN', 'RESTAURANT_ORDERS_CLOSE_BILL']}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={onOpenCustomerSelector}
                    className="shrink-0 rounded-md border border-primary/40 px-2 py-1 text-[11px] font-semibold text-primary transition hover:bg-primary/10 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Cambiar
                  </button>
                </Can>
              )}
            </div>

            <div className="flex items-center gap-2 rounded-lg border border-stroke bg-white px-3 py-2 dark:border-strokedark dark:bg-boxdark">
              <span className="rounded-md bg-primary/10 p-1.5 text-primary">
                <UserCircle className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-bodydark2">
                  Mesero
                </p>
                <p className="truncate text-xs font-medium text-black dark:text-white">
                  {order.waiterName ||
                    (order.waiterId
                      ? `#${order.waiterId}`
                      : 'Usuario autenticado')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {showToolbar && (
        <Can perm="RESTAURANT_ORDERS_ADVANCED">
        <div className="flex w-full divide-x divide-stroke border-b border-stroke bg-white dark:divide-strokedark dark:border-strokedark dark:bg-boxdark">
            {posFlags?.allowTableTransfer && order?.tableId != null && (
              <button
                type="button"
                disabled={busy}
                onClick={onTransferTable}
                title="Trasladar la orden a otra mesa"
                className="flex flex-1 items-center justify-center gap-1.5 px-2 py-2 text-[11px] font-semibold text-body transition hover:bg-primary/5 hover:text-primary active:bg-primary/10 disabled:opacity-40 dark:text-bodydark"
              >
                <Move className="h-4 w-4" />
                Mesa
              </button>
            )}
            {posFlags?.allowMoveOrderItems && (
              <button
                type="button"
                disabled={busy}
                onClick={onMoveItems}
                title="Mover productos a otra orden"
                className="flex flex-1 items-center justify-center gap-1.5 px-2 py-2 text-[11px] font-semibold text-body transition hover:bg-primary/5 hover:text-primary active:bg-primary/10 disabled:opacity-40 dark:text-bodydark"
              >
                <ArrowRightLeft className="h-4 w-4" />
                Productos
              </button>
            )}
            {posFlags?.allowMergeOrders && (
              <button
                type="button"
                disabled={busy}
                onClick={onMergeOrders}
                title="Fusionar con otra orden"
                className="flex flex-1 items-center justify-center gap-1.5 px-2 py-2 text-[11px] font-semibold text-body transition hover:bg-primary/5 hover:text-primary active:bg-primary/10 disabled:opacity-40 dark:text-bodydark"
              >
                <Combine className="h-4 w-4" />
                Fusionar
              </button>
            )}
            {posFlags?.splitBillEnabled && (
              <button
                type="button"
                disabled={busy}
                onClick={onSplitBill}
                title={hasSplit ? 'Cuenta dividida' : 'Dividir la cuenta'}
                className={`flex flex-1 items-center justify-center gap-1.5 px-2 py-2 text-[11px] font-semibold transition active:bg-primary/10 disabled:opacity-40 ${
                  hasSplit
                    ? 'bg-primary/10 text-primary'
                    : 'text-body hover:bg-primary/5 hover:text-primary dark:text-bodydark'
                }`}
              >
                <Split className="h-4 w-4" />
                Dividir
              </button>
            )}
        </div>
        </Can>
      )}

      {busy && !order ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      ) : serviceType === 'DINE_IN' && !selectedTable ? (
        <div className="flex flex-1 items-center justify-center p-6">
          <div className="flex w-full max-w-xs flex-col items-center rounded-xl border border-stroke bg-gray-2/40 px-6 py-10 text-center dark:border-strokedark dark:bg-meta-4/20">
            <span className="mb-3 rounded-full bg-primary/10 p-3 text-primary">
              <ClipboardList className="h-7 w-7" />
            </span>
            <p className="font-medium text-black dark:text-white">
              Ninguna mesa seleccionada
            </p>
            <p className="mt-1 text-sm text-bodydark2">
              Elija una mesa libre para abrir una orden o una ocupada para
              continuarla.
            </p>
          </div>
        </div>
      ) : !order ? (
        <div className="flex flex-1 flex-col p-4">
          <div className="rounded-xl border border-primary/15 bg-primary/5 p-4">
            <p className="font-medium text-black dark:text-white">
              {serviceType === 'DINE_IN'
                ? 'Abrir nueva orden'
                : `Nueva orden · ${serviceTypeLabels[serviceType]}`}
            </p>

            <p className="mt-1 text-xs text-bodydark2">
              {canChooseWaiter
                ? 'Se asigna el cliente predeterminado; podés cambiar cliente y mesero.'
                : 'Se asigna el cliente predeterminado y tu usuario como mesero.'}
            </p>
          </div>

          <div className="mt-5">
            <SelectField
              label="Cliente"
              name="orderCustomer"
              value={customerId}
              iconKey="user"
              placeholder="Selecciona un cliente"
              options={customers.map((item) => ({
                value: item.id,
                label: item.defaultCustomer
                  ? `${item.name} · Predeterminado`
                  : item.name,
              }))}
              onChange={(event) =>
                onCustomerChange(Number(event.target.value))
              }
            />

            {canChooseWaiter ? (
              <SelectField
                label="Mesero"
                name="orderWaiter"
                value={waiterId}
                iconKey="user"
                placeholder="Se asigna automáticamente"
                options={[
                  { value: 0, label: 'Se asigna automáticamente' },
                  ...waiters.map((item) => ({
                    value: item.id,
                    label: item.name,
                  })),
                ]}
                onChange={(event) =>
                  onWaiterChange(
                    Number(event.target.value) === 0
                      ? ''
                      : Number(event.target.value)
                  )
                }
              />
            ) : (
              <div className="mb-4">
                <span className="mb-2 block text-sm font-medium text-black dark:text-white">
                  Mesero
                </span>
                <div className="flex items-center gap-2 rounded-xl border border-stroke bg-gray-2/40 px-4 py-3 dark:border-strokedark dark:bg-meta-4/20">
                  <span className="rounded-md bg-primary/10 p-1.5 text-primary">
                    <UserCircle className="h-4 w-4" />
                  </span>
                  <span className="truncate text-sm text-black dark:text-white">
                    {sessionUserLabel || 'Tu usuario de la sesión'}
                  </span>
                </div>
              </div>
            )}

            <label className="mb-2 block text-sm font-medium text-black dark:text-white">
              Notas (opcional)
            </label>

            <textarea
              rows={3}
              maxLength={500}
              value={notes}
              onChange={(event) => onNotesChange(event.target.value)}
              className="w-full rounded-xl border border-stroke bg-transparent px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10 dark:border-strokedark dark:text-white"
              placeholder="Indicaciones generales..."
            />
          </div>

          <div className="mt-auto">
            <Can
              perm="RESTAURANT_ORDERS_OPEN"
              fallback={
                <p className="rounded-lg bg-gray-2 px-4 py-3 text-center text-xs text-bodydark2 dark:bg-meta-4">
                  No tiene permiso para abrir órdenes. Seleccione una mesa
                  ocupada para cobrarla.
                </p>
              }
            >
              <ActionButton
                label="Abrir orden"
                icon={
                  busy ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Plus className="h-5 w-5" />
                  )
                }
                variant="primary"
                size="lg"
                disabled={busy}
                onClick={onCreate}
              />
            </Can>
          </div>
        </div>
      ) : (
        <>
          <div className="flex-1 space-y-3 overflow-y-auto p-4">
            {hasSplit && order.status === 'OPEN' && (
              <p className="rounded-lg border border-warning/40 bg-warning/5 px-3 py-2 text-xs text-warning">
                La cuenta está dividida. Quitá la división para agregar, quitar o
                cambiar productos.
              </p>
            )}

            {orderRows.map((row) => {
              if (row.type === 'combo') {
                const canRemoveCombo =
                  order.status === 'OPEN' && !row.sentToKitchen && !hasSplit;

                return (
                  <article
                    key={`combo-${row.key}`}
                    className="overflow-hidden rounded-xl border border-primary/30 bg-primary/5"
                  >
                    <div className="bg-primary px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white">
                      Combo
                    </div>
                    <div className="p-3">
                    {/* Header: 1) estado (icono) · 2) nombre · 3) borrar */}
                    <div className="flex items-center gap-2.5 border-b border-primary/15 pb-2.5 dark:border-primary/10">
                      {kitchenWorkflowEnabled &&
                        (row.sentToKitchen ? (
                          <span
                            className="flex shrink-0 items-center text-success"
                            title="Enviado a cocina"
                          >
                            <UtensilsCrossed className="h-5 w-5" />
                          </span>
                        ) : row.pending ? (
                          <span
                            className="flex shrink-0 items-center text-warning"
                            title="Pendiente de enviar"
                          >
                            <PauseCircle className="h-5 w-5" />
                          </span>
                        ) : null)}

                      <p className="flex-1 truncate font-medium text-black dark:text-white">
                        {row.name}
                      </p>

                      <Can perm="RESTAURANT_ORDERS_OPEN">
                        <button
                          type="button"
                          disabled={busy || !canRemoveCombo}
                          onClick={() => onCancelItem(row.components[0])}
                          aria-label={`Eliminar ${row.name}`}
                          className="shrink-0 rounded-lg p-1.5 text-danger transition hover:bg-danger/10 disabled:cursor-not-allowed disabled:opacity-30"
                          title={
                            canRemoveCombo
                              ? 'Eliminar combo'
                              : 'Combo enviado; no se puede eliminar'
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </Can>
                    </div>

                    <ul className="mt-2 space-y-0.5 text-xs text-bodydark2">
                      {row.components.map((component) => (
                        <li key={component.id}>
                          {component.quantity} × {orderItemName(component)}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-3 flex items-center justify-between">
                      {(() => {
                        const comboQty = Math.max(
                          1,
                          Math.round(
                            Number(row.components[0]?.comboQuantity ?? 1)
                          )
                        );
                        const instanceId =
                          row.components[0]?.comboInstanceId != null
                            ? String(row.components[0].comboInstanceId)
                            : null;
                        const canEditCombo =
                          order.status === 'OPEN' &&
                          !row.sentToKitchen &&
                          !hasSplit &&
                          instanceId != null &&
                          onUpdateComboQuantity != null;

                        return (
                          <Can
                            perm="RESTAURANT_ORDERS_OPEN"
                            fallback={
                              <span className="text-sm font-semibold text-bodydark2">
                                × {comboQty}
                              </span>
                            }
                          >
                          <div className="flex items-center overflow-hidden rounded-lg border border-stroke bg-white dark:border-strokedark dark:bg-boxdark">
                            <button
                              type="button"
                              disabled={busy || !canEditCombo}
                              aria-label={`Disminuir ${row.name}`}
                              title={
                                comboQty <= 1 ? 'Eliminar combo' : undefined
                              }
                              onClick={() =>
                                instanceId &&
                                onUpdateComboQuantity?.(
                                  instanceId,
                                  comboQty - 1
                                )
                              }
                              className={`flex items-center justify-center p-2 transition active:scale-90 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent ${
                                comboQty <= 1
                                  ? 'text-danger hover:bg-danger/10 active:bg-danger/20'
                                  : 'text-bodydark2 hover:bg-primary/10 hover:text-primary active:bg-primary/20 dark:hover:bg-meta-4'
                              }`}
                            >
                              {comboQty <= 1 ? (
                                <Trash2 className="h-3.5 w-3.5" />
                              ) : (
                                <Minus className="h-3.5 w-3.5" />
                              )}
                            </button>
                            <span className="min-w-[36px] text-center text-sm font-semibold">
                              {comboQty}
                            </span>
                            <button
                              type="button"
                              disabled={busy || !canEditCombo}
                              aria-label={`Incrementar ${row.name}`}
                              onClick={() =>
                                instanceId &&
                                onUpdateComboQuantity?.(
                                  instanceId,
                                  comboQty + 1
                                )
                              }
                              className="flex items-center justify-center p-2 text-bodydark2 transition hover:bg-primary/10 hover:text-primary active:scale-90 active:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-bodydark2 dark:hover:bg-meta-4"
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          </Can>
                        );
                      })()}
                      <strong className="text-black dark:text-white">
                        {formatOrderMoney(row.total)}
                      </strong>
                    </div>
                    </div>
                  </article>
                );
              }

              const item = row.item;
              const comboComponent = false;
              const canEditItem =
                order.status === 'OPEN' && item.editable && !hasSplit;
              const pendingKitchen =
                item.editable === true && item.sentToKitchen === false;

              return (
                <article
                  key={item.id}
                  className="rounded-xl border border-stroke bg-gray-2/20 p-3 dark:border-strokedark dark:bg-meta-4/20"
                >
                  {/* Header: 1) estado (icono) · 2) nombre · 3) borrar */}
                  <div className="flex items-center gap-2.5 border-b border-stroke/70 pb-2.5 dark:border-strokedark/70">
                    {kitchenWorkflowEnabled &&
                      (item.sentToKitchen ? (
                        <span
                          className="flex shrink-0 items-center text-success"
                          title="Enviado a cocina"
                        >
                          <UtensilsCrossed className="h-5 w-5" />
                        </span>
                      ) : pendingKitchen ? (
                        <span
                          className="flex shrink-0 items-center text-warning"
                          title="Pendiente de enviar"
                        >
                          <PauseCircle className="h-5 w-5" />
                        </span>
                      ) : null)}

                    <p className="flex-1 truncate font-medium text-black dark:text-white">
                      {orderItemName(item)}
                    </p>

                    <Can perm="RESTAURANT_ORDERS_OPEN">
                      <button
                        type="button"
                        disabled={busy || !canEditItem}
                        onClick={() => onCancelItem(item)}
                        aria-label={`Eliminar ${orderItemName(item)}`}
                        className="shrink-0 rounded-lg p-1.5 text-danger transition hover:bg-danger/10 disabled:cursor-not-allowed disabled:opacity-30"
                        title={
                          canEditItem
                            ? 'Eliminar producto'
                            : 'Producto enviado; no se puede eliminar'
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </Can>
                  </div>

                  <div className="mt-2">
                    {comboComponent && (
                      <p className="mb-1 text-xs font-semibold uppercase text-primary">
                        {item.comboName || 'Componente de combo'}
                      </p>
                    )}

                    {!!item.modifiers?.length && (
                      <ul className="text-xs text-bodydark2">
                        {item.modifiers.map((modifier) => (
                          <li key={modifier.id}>
                            + {modifier.name} (
                            {formatOrderMoney(modifier.priceAdjustment)})
                          </li>
                        ))}
                      </ul>
                    )}

                    <p className="mt-1 text-xs text-bodydark2">
                      {formatOrderMoney(item.unitPrice ?? item.price)} c/u
                    </p>
                    {Number(item.tipAmount ?? 0) > 0 && (
                      <p className="mt-1 text-xs text-bodydark2">
                        Propina asignada: {formatOrderMoney(item.tipAmount)}
                      </p>
                    )}
                    {Number(item.tourismTaxAmount ?? 0) > 0 && (
                      <p className="mt-1 text-xs text-bodydark2">
                        Turismo 5 %: {formatOrderMoney(item.tourismTaxAmount)}
                      </p>
                    )}
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <Can
                      perm="RESTAURANT_ORDERS_OPEN"
                      fallback={
                        <span className="text-sm font-semibold text-bodydark2">
                          × {item.quantity}
                        </span>
                      }
                    >
                      <div className="flex items-center overflow-hidden rounded-lg border border-stroke bg-white dark:border-strokedark dark:bg-boxdark">
                        <button
                          type="button"
                          disabled={busy || !canEditItem || item.quantity <= 0}
                          aria-label={`Disminuir ${orderItemName(item)}`}
                          title={
                            item.quantity <= 1 ? 'Eliminar producto' : undefined
                          }
                          onClick={() =>
                            onUpdateQuantity(item.id, item.quantity - 1)
                          }
                          className={`flex items-center justify-center p-2 transition active:scale-90 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent ${
                            item.quantity <= 1
                              ? 'text-danger hover:bg-danger/10 active:bg-danger/20'
                              : 'text-bodydark2 hover:bg-primary/10 hover:text-primary active:bg-primary/20 dark:hover:bg-meta-4'
                          }`}
                        >
                          {item.quantity <= 1 ? (
                            <Trash2 className="h-3.5 w-3.5" />
                          ) : (
                            <Minus className="h-3.5 w-3.5" />
                          )}
                        </button>

                        <span className="min-w-[36px] text-center text-sm font-semibold">
                          {item.quantity}
                        </span>

                        <button
                          type="button"
                          disabled={busy || !canEditItem}
                          aria-label={`Incrementar ${orderItemName(item)}`}
                          onClick={() =>
                            onUpdateQuantity(item.id, item.quantity + 1)
                          }
                          className="flex items-center justify-center p-2 text-bodydark2 transition hover:bg-primary/10 hover:text-primary active:scale-90 active:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-bodydark2 dark:hover:bg-meta-4"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </Can>

                    <strong className="text-black dark:text-white">
                      {formatOrderMoney(item.subtotal ?? item.total)}
                    </strong>
                  </div>
                </article>
              );
            })}

            {activeItems.length === 0 && (
              <div className="rounded-xl border border-dashed border-stroke bg-gray-2/40 py-12 text-center dark:border-strokedark dark:bg-meta-4/20">
                <ClipboardList className="mx-auto mb-3 h-8 w-8 text-bodydark2" />
                <p className="text-sm text-bodydark2">
                  La orden aún no tiene productos.
                </p>
              </div>
            )}
          </div>

          <div className="border-t border-stroke bg-gray-2/40 p-4 dark:border-strokedark dark:bg-meta-4/10">
            {kitchenPrintStates.length > 0 && (
              <div
                className="mb-4 space-y-1.5 rounded-lg border border-stroke p-3 text-xs dark:border-strokedark"
                aria-live="polite"
              >
                {kitchenPrintStates.map((printState) => (
                  <p key={printState.jobId}>
                    <span className="font-semibold">
                      {printState.preparationStationName}:
                    </span>{' '}
                    {printState.status === 'PRINTING'
                      ? 'imprimiendo'
                      : printState.status === 'PRINTED'
                      ? 'impreso'
                      : `error${
                          printState.error ? ` · ${printState.error}` : ''
                        }`}
                  </p>
                ))}
              </div>
            )}

            <div className="mb-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-bodydark2">Productos</span>
                <span>{activeItems.length}</span>
              </div>

              {order.subtotal !== undefined && (
                <div className="flex justify-between text-sm">
                  <span className="text-bodydark2">Subtotal</span>
                  <span>{formatOrderMoney(order.subtotal)}</span>
                </div>
              )}

              {Number(order.totalTourismTax ?? 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-bodydark2">
                    Contribución de turismo (5 %)
                  </span>
                  <span>{formatOrderMoney(order.totalTourismTax)}</span>
                </div>
              )}

              {Number(order.totalTip ?? 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-bodydark2">Propina</span>
                  <span>{formatOrderMoney(order.totalTip)}</span>
                </div>
              )}

              <div className="flex justify-between text-lg font-semibold text-black dark:text-white">
                <span>Total</span>
                <span>{formatOrderMoney(order.total)}</span>
              </div>
            </div>

            {(order.status === 'OPEN' ||
              order.status === 'CONFIRMED' ||
              order.status === 'IN_KITCHEN') && (
              <div>
                {!hasSplit && (
                  <Can perm="RESTAURANT_ORDERS_OPEN">
                    <ActionButton
                      label="Agregar productos"
                      icon={<Plus className="h-4 w-4" />}
                      variant="outline"
                      size="md"
                      className="mb-2 xl:hidden"
                      disabled={busy}
                      onClick={onOpenMenu}
                    />
                  </Can>
                )}

                {order.status !== 'OPEN' ? (
                  // Ya se envió una comanda y sigue en preparación: solo se
                  // puede seguir agregando (arriba) y, si algo quedó
                  // pendiente, enviarlo como una nueva comanda. No se puede
                  // cancelar ni cobrar hasta que esté Lista.
                  hasPendingItems && (
                    <Can perm="RESTAURANT_ORDERS_CONFIRM">
                      <ActionButton
                        label="Enviar comanda"
                        icon={<CheckCircle2 className="h-4 w-4" />}
                        variant="success"
                        size="md"
                        disabled={busy}
                        onClick={onConfirmOrder}
                      />
                    </Can>
                  )
                ) : !kitchenWorkflowEnabled ? (
                  <div className="flex gap-2">
                    <Can perm="RESTAURANT_ORDERS_CANCEL">
                      <ActionButton
                        className="flex-1"
                        label="Cancelar orden"
                        variant="danger"
                        size="md"
                        disabled={busy}
                        onClick={onCancelOrder}
                      />
                    </Can>
                    <Can perm="RESTAURANT_ORDERS_CLOSE_BILL">
                      <ActionButton
                        className="flex-1"
                        label="Cobrar"
                        icon={<CheckCircle2 className="h-4 w-4" />}
                        variant="success"
                        size="md"
                        disabled={busy || activeItems.length === 0}
                        onClick={onOpenBilling}
                      />
                    </Can>
                  </div>
                ) : wasDispatched ? (
                  <Can perm="RESTAURANT_ORDERS_CONFIRM">
                    <div className="flex gap-2">
                      <ActionButton
                        className="flex-1"
                        label="Volver a cobro"
                        variant="outline"
                        size="md"
                        disabled={busy || hasPendingItems}
                        title={
                          hasPendingItems
                            ? 'Enviá la comanda o quitá los productos nuevos primero'
                            : undefined
                        }
                        onClick={onConfirmOrder}
                      />
                      <ActionButton
                        className="flex-1"
                        label="Enviar comanda"
                        icon={<CheckCircle2 className="h-4 w-4" />}
                        variant="success"
                        size="md"
                        disabled={busy || !hasPendingItems}
                        onClick={onConfirmOrder}
                      />
                    </div>
                  </Can>
                ) : (
                  <div className="flex gap-2">
                    <Can perm="RESTAURANT_ORDERS_CANCEL">
                      <ActionButton
                        className="flex-1"
                        label="Cancelar orden"
                        variant="danger"
                        size="md"
                        disabled={busy}
                        onClick={onCancelOrder}
                      />
                    </Can>
                    <Can perm="RESTAURANT_ORDERS_CONFIRM">
                      <ActionButton
                        className="flex-1"
                        label="Enviar comanda"
                        icon={<CheckCircle2 className="h-4 w-4" />}
                        variant="success"
                        size="md"
                        disabled={busy || !hasPendingItems}
                        onClick={onConfirmOrder}
                      />
                    </Can>
                  </div>
                )}
              </div>
            )}

            {/* Cada acción de cobro se gatea adentro (Cobrar/Continuar/
                Reintentar -> CLOSE_BILL, Agregar más -> OPEN), no acá, para
                que un mesero sin CLOSE_BILL siga viendo "Agregar más". */}
            <RestaurantOrderBillingActions
              orderStatus={order.status}
              billing={billing}
              billingMessage={billingMessage}
              loading={loadingBilling}
              starting={startingBilling}
              reopening={busy}
              onOpen={onOpenBilling}
              onAddMore={onAddMore}
              onContinue={onContinueBilling}
            />
          </div>
        </>
      )}
    </section>
  );
};

export default CurrentOrderPanel;
